import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EntitySelect } from '../../components/EntityField';
import { type NumberInputValue, typedNumber } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { RingsUpAt } from './RingsUpAt';

const ITEMS_PATH = '/admin/v1/catalog/items';
const BUNDLES_PATH = '/admin/v1/catalog/bundles';
const VARIANTS_PATH = '/admin/v1/catalog/variants';

interface VariantRow {
  id: string;
  menuItemId: string;
  nameEn: string;
  isDefault: boolean;
  active: boolean;
}

interface BundleFull {
  bundle: {
    id: string;
    nameEn: string;
    nameEs: string | null;
    descriptionEn: string | null;
    active: boolean;
  };
  slots: { slot: { id: string; nameEn: string; quantity: number }; options: { variantId: string }[] }[];
}

/**
 * Sizes can only be listed under the item that owns them, so a combo choice is picked in two
 * steps: the item first, then the size. Where an item has only one size the step is skipped for
 * the user — the picker fills itself in and stays visible so the choice is still readable.
 */
function ChoicePicker({
  menuItemId,
  variantId,
  onChange,
}: {
  menuItemId: string;
  variantId: string;
  onChange: (next: { menuItemId: string; variantId: string }) => void;
}) {
  const variants = useQuery({
    queryKey: ['variants', menuItemId],
    queryFn: () => rawApi<VariantRow[]>('GET', `${ITEMS_PATH}/${menuItemId}/variants`),
    enabled: Boolean(menuItemId),
    staleTime: 60_000,
  });

  const options = (variants.data ?? [])
    .filter((v) => v.active !== false)
    .map((v) => ({ value: v.id, label: v.nameEn || 'Standard' }));

  // One size means there is nothing to decide; choosing it here saves a tap on every single line
  // of every combo, which is where this form is otherwise tedious enough to be abandoned.
  const only = options.length === 1 ? options[0]!.value : null;

  // The parent rebuilds `onChange` on every keystroke anywhere in the form, so it is held behind a
  // ref: naming it as a dependency below would re-run the fill on every render instead of once.
  const report = useRef(onChange);
  useEffect(() => {
    report.current = onChange;
  }, [onChange]);
  useEffect(() => {
    if (only && variantId !== only) report.current({ menuItemId, variantId: only });
  }, [only, variantId, menuItemId]);

  return (
    <>
      <div style={{ minWidth: 240, flex: 1 }}>
        <EntitySelect
          entity="menuItem"
          label="Item"
          value={menuItemId || null}
          onChange={(v) => onChange({ menuItemId: v ?? '', variantId: '' })}
        />
      </div>
      <Select
        label="Size"
        style={{ width: 200 }}
        data={options}
        value={variantId || only || null}
        onChange={(v) => onChange({ menuItemId, variantId: v ?? '' })}
        disabled={!menuItemId || variants.isLoading}
        placeholder={!menuItemId ? 'Pick the item first' : variants.isLoading ? 'Loading…' : 'Choose a size'}
        nothingFoundMessage="This item has no sizes set up"
        comboboxProps={{ withinPortal: true }}
      />
    </>
  );
}

interface ComboChoice {
  /** Rows are added and removed while the form is open, so each carries its own React key. */
  key: string;
  menuItemId: string;
  variantId: string;
}

interface ComboSlot {
  key: string;
  nameEn: string;
  quantity: NumberInputValue;
  choices: ComboChoice[];
}

let keySeq = 0;
const newKey = () => `row-${++keySeq}`;

const emptyChoice = (): ComboChoice => ({ key: newKey(), menuItemId: '', variantId: '' });
const emptySlot = (): ComboSlot => ({
  key: newKey(),
  nameEn: '',
  quantity: 1,
  choices: [emptyChoice()],
});

/** What travels to the server, and the shape compared against to see whether the picks changed. */
const slotsForWire = (slots: ComboSlot[]) =>
  slots.map((s) => ({
    nameEn: s.nameEn.trim(),
    quantity: typedNumber(s.quantity) ?? 1,
    variantIds: s.choices.map((c) => c.variantId).filter(Boolean),
  }));

/**
 * A combo, opened.
 *
 * A combo is a name and a list of picks: "a bucket of six", "pick a side". Each pick offers the
 * guest a set of things off the menu, which is why this cannot be an ordinary form — the shape is
 * a list inside a list, and the innermost value is a size that only exists under its own item.
 *
 * The same dialog builds one and changes one. It used to only build: what was inside a combo could
 * never be read back, so nobody could confirm the beer bucket held six of the right beers, and the
 * only way to add one was to make a second bucket beside it and hope staff picked the new one.
 */
export function ComboModal({
  opened,
  bundleId,
  locationId,
  onClose,
}: {
  opened: boolean;
  /** Null when building a new combo. */
  bundleId: string | null;
  locationId: string | undefined;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [nameEn, setNameEn] = useState('');
  const [nameEs, setNameEs] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [active, setActive] = useState(true);
  const [slots, setSlots] = useState<ComboSlot[]>([emptySlot()]);
  const [error, setError] = useState<string | null>(null);
  /** What the server last told us the picks were, so a rename does not rewrite every pick row. */
  const savedPicks = useRef<string>('');

  const existing = useQuery({
    queryKey: ['combo', bundleId],
    queryFn: () => rawApi<BundleFull>('GET', `${BUNDLES_PATH}/${bundleId}`),
    enabled: opened && Boolean(bundleId),
  });

  // A pick stores only the size; the item above it has to be looked up, or every row of an
  // existing combo would open with an empty "Item" box and no way to tell what it used to be.
  const sizes = useQuery({
    queryKey: listKey(VARIANTS_PATH),
    queryFn: () => rawApi<VariantRow[]>('GET', VARIANTS_PATH),
    enabled: opened && Boolean(bundleId),
    staleTime: 60_000,
  });

  const loaded = existing.data;
  const sizeRows = sizes.data;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const seedKey = opened ? (bundleId ?? 'new') : null;

  useEffect(() => {
    if (seedKey === seededFor) return;
    if (!seedKey) {
      setSeededFor(null);
      return;
    }
    if (seedKey === 'new') {
      setSeededFor(seedKey);
      setNameEn('');
      setNameEs('');
      setDescriptionEn('');
      setActive(true);
      setSlots([emptySlot()]);
      savedPicks.current = '';
      setError(null);
      return;
    }
    // Never seed from a cached copy that is being refreshed behind it: reopening a combo someone
    // else changed would otherwise fill the form from the old version, and saving would put the
    // old picks back.
    if (!loaded || !sizeRows || existing.isFetching) return;
    setSeededFor(seedKey);
    setNameEn(loaded.bundle.nameEn);
    setNameEs(loaded.bundle.nameEs ?? '');
    setDescriptionEn(loaded.bundle.descriptionEn ?? '');
    setActive(loaded.bundle.active);
    const seeded = loaded.slots.map(({ slot, options }) => ({
      key: newKey(),
      nameEn: slot.nameEn,
      quantity: slot.quantity as NumberInputValue,
      choices: options.length
        ? options.map((o) => ({
            key: newKey(),
            menuItemId: sizeRows.find((v) => v.id === o.variantId)?.menuItemId ?? '',
            variantId: o.variantId,
          }))
        : [emptyChoice()],
    }));
    setSlots(seeded.length ? seeded : [emptySlot()]);
    savedPicks.current = JSON.stringify(slotsForWire(seeded.length ? seeded : [emptySlot()]));
    setError(null);
  }, [seedKey, seededFor, loaded, sizeRows, existing.isFetching]);

  const close = () => {
    setSeededFor(null);
    onClose();
  };

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: listKey(BUNDLES_PATH) });
    void queryClient.invalidateQueries({ queryKey: ['combo', bundleId] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const picks = slotsForWire(slots);
      if (!bundleId) {
        return rawApi('POST', BUNDLES_PATH, {
          locationId,
          nameEn: nameEn.trim(),
          nameEs: nameEs.trim() || null,
          descriptionEn: descriptionEn.trim() || null,
          slots: picks,
        });
      }
      await rawApi('PATCH', `${BUNDLES_PATH}/${bundleId}`, {
        nameEn: nameEn.trim(),
        nameEs: nameEs.trim() || null,
        descriptionEn: descriptionEn.trim() || null,
        active,
      });
      // Only when they actually differ: replacing the picks rewrites every row, and a combo
      // renamed on a Tuesday should not read in the history as though its contents changed.
      if (JSON.stringify(picks) !== savedPicks.current) {
        await rawApi('PUT', `${BUNDLES_PATH}/${bundleId}/slots`, { slots: picks });
      }
      return null;
    },
    onSuccess: () => {
      refresh();
      notifyDone(
        bundleId ? 'Combo saved' : 'Combo added',
        `${nameEn.trim()} — send the menu to the registers when you are ready for staff to see it.`,
      );
      close();
    },
    onError: (err) => notifyError(err, bundleId ? 'save this combo' : 'add this combo'),
  });

  const submit = () => {
    if (!bundleId && !locationId) return;
    if (!nameEn.trim()) return setError('Give the combo a name');
    if (slots.length === 0) return setError('A combo needs at least one pick');
    for (const slot of slots) {
      if (!slot.nameEn.trim()) return setError('Every pick needs a name, such as "Choose a beer"');
      if (slot.choices.every((c) => !c.variantId)) {
        return setError(`"${slot.nameEn.trim()}" has nothing the guest can choose from`);
      }
    }
    setError(null);
    save.mutate();
  };

  const setSlot = (index: number, patch: Partial<ComboSlot>) =>
    setSlots(slots.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  // Until this combo's own contents are in the form, show a spinner rather than the last combo
  // someone opened — a form briefly displaying the wrong beers is worse than a moment of nothing.
  const loading =
    Boolean(bundleId) &&
    (existing.isFetching || sizes.isLoading || seededFor !== seedKey) &&
    !existing.isError;

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={bundleId ? nameEn || 'Combo' : 'Add a combo'}
      size="xl"
      closeOnClickOutside={false}
    >
      {loading ? (
        <Group justify="center" p="xl">
          <Loader size="sm" />
        </Group>
      ) : existing.isError ? (
        <Alert color="red" title="Could not open this combo">
          What is inside it could not be reached. Nothing has been lost — this is only a display problem.
        </Alert>
      ) : (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            A combo rings up as one thing but is made of picks. Name each pick the way staff would say it at
            the register, then list what the guest can choose from.
          </Text>

          {/* A combo is priced as a whole, and nothing here used to say so — so a beer bucket could
              be built, sent to the registers and be unringable, with the reason two screens away. Asked
              of the pricing engine, so it is the figure staff would actually see, and set here,
              because the combo it belongs to is what this dialog is already about. */}
          {bundleId ? (
            <Group gap="xs" align="center">
              <Text size="sm">Rings up at</Text>
              <RingsUpAt targetType="bundle" targetId={bundleId} name={nameEn} />
              <Text size="xs" c="dimmed">
                — tap the price to change it
              </Text>
            </Group>
          ) : (
            <Text size="xs" c="dimmed">
              You can put a price on it as soon as it is added — until it has one, staff cannot ring it up.
            </Text>
          )}

          <TextInput
            label="Name"
            required
            placeholder="Beer bucket"
            value={nameEn}
            onChange={(e) => setNameEn(e.currentTarget.value)}
          />
          <TextInput
            label="Name in Spanish"
            description="Used on registers and tickets set to Spanish"
            value={nameEs}
            onChange={(e) => setNameEs(e.currentTarget.value)}
          />
          <TextInput
            label="Description"
            description="What the guest gets, in a line"
            placeholder="Six cold beers, your pick"
            value={descriptionEn}
            onChange={(e) => setDescriptionEn(e.currentTarget.value)}
          />

          {bundleId && (
            <Switch
              checked={active}
              onChange={(e) => setActive(e.currentTarget.checked)}
              label={active ? 'Selling — staff can ring this up' : 'Not selling — hidden from the registers'}
              description="A set menu you only run at Christmas lives here the rest of the year."
            />
          )}

          <div>
            <Text size="sm" fw={500}>
              Picks
            </Text>
            <Text size="xs" c="dimmed" mb={6}>
              One for each thing the guest chooses. A beer bucket is one pick with a quantity of six.
            </Text>
            <Stack gap="sm">
              {slots.map((slot, slotIndex) => (
                <Paper key={slot.key} withBorder p="sm" radius="sm">
                  <Group gap="xs" align="flex-end" wrap="wrap" mb="xs">
                    <TextInput
                      label="What staff see"
                      placeholder="Choose a beer"
                      style={{ minWidth: 240, flex: 1 }}
                      value={slot.nameEn}
                      onChange={(e) => setSlot(slotIndex, { nameEn: e.currentTarget.value })}
                    />
                    <NumberInput
                      label="How many"
                      min={1}
                      style={{ width: 130 }}
                      value={slot.quantity}
                      onChange={(v) => setSlot(slotIndex, { quantity: v })}
                    />
                    <Button
                      size="compact-sm"
                      variant="subtle"
                      color="red"
                      disabled={slots.length === 1}
                      onClick={() => setSlots(slots.filter((_, i) => i !== slotIndex))}
                    >
                      Remove pick
                    </Button>
                  </Group>

                  <Text size="xs" c="dimmed" mb={4}>
                    What the guest can choose from
                  </Text>
                  <Stack gap="xs">
                    {slot.choices.map((choice, choiceIndex) => (
                      <Group key={choice.key} gap="xs" align="flex-end" wrap="wrap">
                        <ChoicePicker
                          menuItemId={choice.menuItemId}
                          variantId={choice.variantId}
                          onChange={(next) =>
                            setSlot(slotIndex, {
                              choices: slot.choices.map((c, i) =>
                                i === choiceIndex ? { ...c, ...next } : c,
                              ),
                            })
                          }
                        />
                        <Button
                          size="compact-sm"
                          variant="subtle"
                          color="red"
                          disabled={slot.choices.length === 1}
                          onClick={() =>
                            setSlot(slotIndex, {
                              choices: slot.choices.filter((_, i) => i !== choiceIndex),
                            })
                          }
                        >
                          Remove
                        </Button>
                      </Group>
                    ))}
                    <Button
                      size="compact-sm"
                      variant="light"
                      style={{ alignSelf: 'flex-start' }}
                      onClick={() => setSlot(slotIndex, { choices: [...slot.choices, emptyChoice()] })}
                    >
                      Add something to choose from
                    </Button>
                  </Stack>
                </Paper>
              ))}
              <Button
                size="compact-sm"
                variant="light"
                style={{ alignSelf: 'flex-start' }}
                onClick={() => setSlots([...slots, emptySlot()])}
              >
                Add another pick
              </Button>
            </Stack>
          </div>

          {error && (
            <Text size="sm" c="red">
              {error}
            </Text>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={close} disabled={save.isPending}>
              Cancel
            </Button>
            <Button loading={save.isPending} onClick={submit}>
              {bundleId ? 'Save combo' : 'Add combo'}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
